import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_COMMITTEEMEMBERROLE_LOAD_BY_ID,
    ACTION_COMMITTEEMEMBERROLE_LOAD_BY_COMMITTEE_ID,
    ACTION_COMMITTEEMEMBERROLE_LOAD_BY_PERSONNEL_ID,
    ACTION_COMMITTEEMEMBERROLE_LOAD_FAILURE,
    ACTION_COMMITTEEMEMBERROLE_LOAD_SUCCESS,

    ACTION_COMMITTEEMEMBERROLE_SAVE,
    ACTION_COMMITTEEMEMBERROLE_SAVE_SUCCESS,
    ACTION_COMMITTEEMEMBERROLE_SAVE_FAILURE,

    ACTION_COMMITTEEMEMBERROLE_CREATE,
    ACTION_COMMITTEEMEMBERROLE_CREATE_SUCCESS,
    ACTION_COMMITTEEMEMBERROLE_CREATE_FAILURE,

    ACTION_COMMITTEEMEMBERROLE_VALIDATE,
    ACTION_COMMITTEEMEMBERROLE_VALIDATE_SUCCESS,
    ACTION_COMMITTEEMEMBERROLE_VALIDATE_FAILURE,

    ACTION_COMMITTEEMEMBERROLE_CLEAR,
    ACTION_COMMITTEEMEMBERROLE_FILTER,

    CommitteeMemberRoleAction
} from '../actions/committeeMemberRole';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ICommitteeMemberRoleState {
    formData: Dtos.CommitteeMemberRole | Dtos.CommitteeMemberRole[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.CommitteeMemberRole[];
    formLength: number;
}

const initialState: ICommitteeMemberRoleState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ICommitteeMemberRoleState, _isLoading: boolean): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ICommitteeMemberRoleState,
    formData: Dtos.CommitteeMemberRole | Dtos.CommitteeMemberRole[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ICommitteeMemberRoleState, responseStatus: Dtos.ResponseStatus): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ICommitteeMemberRoleState, formFilter: IFormFilter): ICommitteeMemberRoleState {

    const items: Dtos.CommitteeMemberRole[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            (p.personnel.displayWithSalutation && p.personnel.displayWithSalutation.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
        )) : undefined;

    const newState: ICommitteeMemberRoleState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ICommitteeMemberRoleState, propertiesToClear?: string[]): ICommitteeMemberRoleState {

    if (!propertiesToClear) {
        const newState: ICommitteeMemberRoleState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ICommitteeMemberRoleState, _isSaving: boolean): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ICommitteeMemberRoleState,
    formData: Dtos.CommitteeMemberRole | Dtos.CommitteeMemberRole[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ICommitteeMemberRoleState, responseStatus: Dtos.ResponseStatus): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ICommitteeMemberRoleState, _isCreating: boolean): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ICommitteeMemberRoleState,
    formData: Dtos.CommitteeMemberRole,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ICommitteeMemberRoleState, responseStatus: Dtos.ResponseStatus): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ICommitteeMemberRoleState, _isValidating: boolean): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ICommitteeMemberRoleState,
    formData: Dtos.CommitteeMemberRole,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ICommitteeMemberRoleState, responseStatus: Dtos.ResponseStatus): ICommitteeMemberRoleState {
    const newState: ICommitteeMemberRoleState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const CommitteeMemberRoleReducer: Reducer<ICommitteeMemberRoleState> = (state: ICommitteeMemberRoleState = initialState, action: CommitteeMemberRoleAction) => {
    switch (action.type) {
        case ACTION_COMMITTEEMEMBERROLE_LOAD_BY_ID:
        case ACTION_COMMITTEEMEMBERROLE_LOAD_BY_COMMITTEE_ID:
        case ACTION_COMMITTEEMEMBERROLE_LOAD_BY_PERSONNEL_ID:
            return loading(state, true);
        case ACTION_COMMITTEEMEMBERROLE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_COMMITTEEMEMBERROLE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEMBERROLE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_COMMITTEEMEMBERROLE_SAVE:
            return saving(state, true);
        case ACTION_COMMITTEEMEMBERROLE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEMEMBERROLE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEMBERROLE_CREATE:
            return creating(state, true);
        case ACTION_COMMITTEEMEMBERROLE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_COMMITTEEMEMBERROLE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEMBERROLE_VALIDATE:
            return validating(state, true);
        case ACTION_COMMITTEEMEMBERROLE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEMEMBERROLE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEMBERROLE_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default CommitteeMemberRoleReducer;